import {
  AllocationPutRequest,
  CommentPost,
  NotePost,
  PortStatus,
  PortStatusPost,
  PortSubmission,
  PortSubmissionPost,
  ProductTable,
  ServiceCharge,
  ServiceChargePost,
  Ticket,
  TicketFilterRequest,
} from "callable-data-objects";
import AllocationPostRequest from "callable-data-objects/dist/models/ticket/AllocationPostRequest";
import AssignmentPutRequest from "callable-data-objects/dist/models/ticket/AssignmentPutRequest";
import EmailPut from "callable-data-objects/dist/models/ticket/EmailPut";
import ExportPostRequest from "callable-data-objects/dist/models/ticket/ExportPostRequest";
import ExportPutRequest from "callable-data-objects/dist/models/ticket/ExportPutRequest";
import MigrationPostRequest from "callable-data-objects/dist/models/ticket/MigrationPostRequest";
import MigrationPutRequest from "callable-data-objects/dist/models/ticket/MigrationPutRequest";
import GlobalNumberPostRequest from "callable-data-objects/dist/models/ticket/GlobalNumberPostRequest";
import Page from "callable-data-objects/dist/services/Page";
import { APITicketContext } from "Providers/APIContexts";
import { APITicketProps, DefaultProviderProps } from "Providers/types";
import React, { useEffect, useState } from "react";
import { useAPI } from "./APIProvider";
import useCheckRole from "Hooks/APICheckRole";

const APITicketProvider = ({ children }: DefaultProviderProps) => {
  const api = useAPI();
  const { hasRole, roles } = useCheckRole();

  const getAllowedTypes = (): string[] => {
    const allowedTypes: string[] = [];
    if (hasRole(roles.ticket.types.assignment)) {
      allowedTypes.push("assignment-request");
    }
    if (hasRole(roles.ticket.types.allocation)) {
      allowedTypes.push("allocation-request");
    }
    if (hasRole(roles.ticket.types.porting)) {
      allowedTypes.push("port-request");
    }
    if (hasRole(roles.ticket.types.export)) {
      allowedTypes.push("export-request");
    }
    if (hasRole(roles.ticket.types.globalNumber)) {
      allowedTypes.push("global-number-request");
    }
    if (hasRole(roles.ticket.types.migration)) {
      allowedTypes.push("migration-request");
    }

    return allowedTypes;
  };

  const [tickets, setTickets] = useState<Page<Ticket> | undefined>();
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [ticket, setTicket] = useState<Ticket | undefined>();
  const [ticketProductTable, setTicketProductTable] = useState<
    ProductTable | undefined
  >();
  const [loading, setLoading] = useState<boolean>(false);
  const [create, setCreate] = useState<string | undefined>();
  const [filterRequest, setFilterRequest] = useState<TicketFilterRequest>(
    () => {
      const filterString = localStorage.getItem("ticketFilter");
      if (filterString) {
        return JSON.parse(filterString);
      }
      return {
        page: 0,
        max: 20,
        sortField: "caseId",
        sortDirection: "DESC",
        status: ["open", "pending", "closed"],
        type: getAllowedTypes(),
      };
    }
  );
  const [filterSubmissionRequest, setFilterSubmissionRequest] = useState<any>(
    () => {
      const filterString = localStorage.getItem("ticketSubmissionFilter");
      if (filterString) {
        return JSON.parse(filterString);
      }
      return {
        status: [
          "awaiting-response",
          "accepted",
          "date-change-requested",
          "activated",
          "cancelled",
          "rejected",
          "provisioned",
          "pov"
        ],
      };
    }
  );

  const [portCreatedResult, setPortCreatedResult] = useState<Ticket>();
  const [allocationCreatedResult, setAllocationCreatedResult] =
    useState<Ticket>();

  const [charges, setCharges] = useState<ServiceCharge[]>([]);

  useEffect(() => {
    filter();
    if (ticket) {
      getCharges();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRequest, ticket]);

  const getLastStatus = (statuses: PortStatus[]) => {
    if (statuses && statuses.length > 0)
      return statuses[statuses.length - 1].status;
    return "";
  };

  useEffect(() => {
    if (tickets && tickets.content && filterSubmissionRequest) {
      const unwind: any[] = [];
      tickets.content.forEach((tick: Ticket) => {
        tick?.portRequest?.submissions?.forEach((sub: PortSubmission) => {
          if (
            filterSubmissionRequest.status.indexOf(
              getLastStatus(sub.statuses)
            ) > -1 &&
            (!filterSubmissionRequest.customerId ||
              tick.portRequest.customer.id ===
                filterSubmissionRequest.customerId)
          ) {
            const t = {
              customer: tick.portRequest.customer,
              caseId: tick.caseId,
              id: tick.id,
              status: getLastStatus(sub.statuses),
              ...sub,
            };
            unwind.push(t);
          }
        });
      });
      setSubmissions(unwind);
    }
  }, [filterSubmissionRequest, tickets]);

  function search(text: string): void {
    setFilterRequest({
      ...filterRequest,
      text,
    });
  }

  async function filter() {
    setLoading(true);
    const response = await api.ticketService.filter(filterRequest);
    setTickets(response);
    setLoading(false);
  }

  async function getCharges() {
    if (ticket) {
      setLoading(true);
      const response = await api.ticketService.charge(ticket.id).list();
      setCharges(response);
      setLoading(false);
    }
  }

  async function addCharge(payload: ServiceChargePost) {
    if (ticket) {
      await api.ticketService.charge(ticket.id).post("", payload);
      select(ticket.id);
    }
  }

  async function addCharges(payloads: ServiceChargePost[]) {
    if (ticket) {
      setLoading(true);
      for (let index = 0; index < payloads.length; index++) {
        const payload = payloads[index];
        await api.ticketService.charge(ticket.id).post("", payload);
      }
      select(ticket.id);
      setLoading(false);
    }
  }

  async function removeCharge(id: string) {
    if (ticket) {
      await api.ticketService.charge(ticket.id).delete(id);
      select(ticket.id);
    }
  }

  async function select(id: string) {
    // setLoading(true);

    const tResponse = await api.ticketService.get(id);
    setTicket(tResponse);

    const pResponse = await api.ticketService.productTable(id).get();
    setTicketProductTable(pResponse);

    //setLoading(false);
  }

  function unselect() {
    setTicket(undefined);
  }

  function uncreate() {
    setCreate(undefined);
  }

  async function saveSubmission(payload: PortSubmissionPost) {
    if (ticket) {
      console.log("Posting Ticket", payload);
      setLoading(true);
      await api.ticketService.porting().submission(ticket.id).post("", payload);
      await select(ticket.id);
      uncreate();
      filter();
      setLoading(false);
    }
  }

  async function saveSubmissionStatus(
    submissionId: string,
    payload: PortStatusPost
  ) {
    if (ticket) {
      setLoading(true);
      await api.ticketService
        .porting()
        .submission(ticket.id)
        .status(submissionId)
        .post("", payload);

      await select(ticket.id);
      uncreate();
      filter();
      setLoading(false);
    }
  }

  async function saveSubmissionStatusBulk(
    submissionIds: string[],
    payload: PortStatusPost
  ) {
    if (ticket) {
      setLoading(true);
      for (let index = 0; index < submissionIds.length; index++) {
        const id = submissionIds[index];
        await api.ticketService
          .porting()
          .submission(ticket.id)
          .status(id)
          .post("", payload);
      }
      await select(ticket.id);
      setLoading(false);
    }
  }

  async function saveExportRequest(payload: ExportPostRequest) {
    setLoading(true);
    const response = await api.ticketService.export().post("", payload);
    if (response) {
      await select(response.id);
      uncreate();
      filter();
    }
    setLoading(false);
  }

  async function saveGlobalNumberRequest(payload: GlobalNumberPostRequest) {
    setLoading(true);
    const response = await api.ticketService.globalNumber().post("", payload);
    if (response) {
      await select(response.id);
      uncreate();
      filter();
    }
    setLoading(false);
  }

  async function saveMigrationRequest(payload: MigrationPostRequest) {
    setLoading(true);
    const response = await api.ticketService.migration().post("", payload);
    if (response) {
      select(response.id);
      uncreate();
      filter();
    }
    setLoading(false);
  }

  async function saveAllocationRequest(payload: AllocationPostRequest) {
    setLoading(true);
    if (create === "allocation") {
      const response = await api.ticketService.allocation().post("", payload);
      if (response) {
        select(response.id);
        uncreate();
        filter();
      }
    }
    setLoading(false);
  }

  async function updateAssignment(payload: AssignmentPutRequest) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.assignment().put(ticket.id, payload);
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function updateAllocation(payload: AllocationPutRequest) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.allocation().put(ticket.id, payload);
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function updateExport(payload: ExportPutRequest) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.export().put(ticket.id, payload);
      filter();
      setLoading(false);
    }
  }

  async function updateMigration(payload: MigrationPutRequest) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.migration().put(ticket.id, payload);
      filter();
      setLoading(false);
    }
  }

  async function updateEmails(payload: EmailPut) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.email(ticket.id).put("", payload);
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function updateStatus(statusString: string) {
    if (ticket) {
      await api.ticketService.status(ticket.id).update(statusString);
      filter();
    }
  }

  async function closeTicket() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.status(ticket.id).closed();
      select(ticket.id);
      filter();
      setLoading(true);
    }
  }

  async function openTicket() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.status(ticket.id).open();
      select(ticket.id);
      filter();
      setLoading(true);
    }
  }

  async function pendingTicket() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.status(ticket.id).pending();
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function buildPorting() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.porting().build(ticket.id);
      filter();
      select(ticket.id);
      setLoading(false);
    }
  }

  async function buildMigration() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.migration().build(ticket.id);
      filter();
      select(ticket.id);
      setLoading(false);
    }
  }

  async function buildExport() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.export().build(ticket.id);
      filter();
      select(ticket.id);
      setLoading(false);
    }
  }

  async function buildGlobalNumber() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.globalNumber().build(ticket.id);
      filter();
      select(ticket.id);
      setLoading(false);
    }
  }

  async function buildAllocation() {
    if (ticket) {
      setLoading(true);
      await api.ticketService.allocation().build(ticket.id);
      filter();
      select(ticket.id);
      setLoading(false);
    }
  }

  async function addComment(payload: CommentPost) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.comment(ticket.id).post("", payload);
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function addNote(payload: NotePost) {
    if (ticket) {
      setLoading(true);
      await api.ticketService.note(ticket.id).post("", payload);
      select(ticket.id);
      filter();
      setLoading(false);
    }
  }

  async function savePort(values: any) {
    setLoading(true);

    const data = new FormData();

    data.append("loa", values.loa);

    data.append("bill", values.bill);

    if (values.takeover) {
      data.append("takeover", values.takeover);
    }

    data.append("cno", values.cno);
    data.append("customerId", values.customerId);
    data.append("contactName", values.contactName);
    data.append("contactPosition", values.contactPosition);
    data.append("contactTelephone", values.contactTelephone);
    data.append("contactMobile", values.contactMobile);
    data.append("oline1", values.oline1);
    data.append("oline2", values.oline2);
    data.append("olevel1", values.olevel1);
    data.append("olevel2", values.olevel2);
    data.append("opostalCode", values.opostalCode);
    data.append("ocountry", values.ocountry);
    data.append("nline1", values.nline1);
    data.append("nline2", values.nline2);
    data.append("nlevel1", values.nlevel1);
    data.append("nlevel2", values.nlevel2);
    data.append("npostalCode", values.npostalCode);
    data.append("ncountry", values.ncountry);
    data.append("mbn", values.mbn);
    data.append("associatedDDIs", values.associatedDDIs);
    data.append("rangeDDIs", values.rangeDDIs);
    data.append("specialNotes", values.specialNotes);

    const response = await api.ticketService.porting().postFormData("", data);

    if (response) setPortCreatedResult(response);

    setLoading(false);
  }

  async function saveAllocation(values: AllocationPostRequest) {
    setLoading(true);
    const response = await api.ticketService.allocation().post("", values);
    if (response) setAllocationCreatedResult(response);
    setLoading(false);
  }

  async function completePort() {
    setPortCreatedResult(undefined);
    filter();
  }

  async function completeAllocation() {
    setPortCreatedResult(undefined);
    filter();
  }

  function clearFilter() {
    const f = {
      page: 0,
      max: 20,
      sortField: "caseId",
      sortDirection: "DESC",
      status: ["open", "pending", "closed"],
      type: getAllowedTypes(),
    };
    localStorage.setItem("ticketFilter", JSON.stringify(f));
    setFilterRequest(f);
  }

  function doFilter(payload: TicketFilterRequest) {
    localStorage.setItem("ticketFilter", JSON.stringify(payload));
    setFilterRequest(payload);
  }

  function doFilterSubmissions(payload: any) {
    localStorage.setItem("ticketSubmissionFilter", JSON.stringify(payload));
    setFilterSubmissionRequest(payload);
  }

  const props: APITicketProps = {
    filterSubmissionRequest,
    setFilterSubmissionRequest,
    tickets,
    ticket,
    ticketProductTable,
    filterRequest,
    setFilterRequest,
    create,
    loading,
    submissions,
    saveAllocationRequest,
    saveExportRequest,
    saveMigrationRequest,
    updateAssignment,
    updateAllocation,
    updateExport,
    updateMigration,
    updateEmails,
    updateStatus,
    closeTicket,
    openTicket,
    pendingTicket,
    buildPorting,
    buildAllocation,
    buildMigration,
    buildExport,
    search,
    select,
    unselect,
    uncreate,
    addComment,
    addNote,
    savePort,
    portCreatedResult,
    saveAllocation,
    allocationCreatedResult,
    completeAllocation,
    completePort,
    saveGlobalNumberRequest,
    buildGlobalNumber,
    charges,
    addCharge,
    addCharges,
    removeCharge,
    saveSubmission,
    saveSubmissionStatus,
    saveSubmissionStatusBulk,
    doFilter,
    doFilterSubmissions,
    clearFilter,
  };

  return (
    <APITicketContext.Provider value={props}>
      {children}
    </APITicketContext.Provider>
  );
};

export default APITicketProvider;
